import React, { useEffect, useState } from 'react';
import '../styles/AdminPortal.css';
import { FaEdit, FaTrash, FaSave } from 'react-icons/fa';
import axios from 'axios';

export default function AdminPortal() {
  const [guests, setGuests] = useState([]);
  const [plusOnes, setPlusOnes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingGuest, setEditingGuest] = useState(null);
  const [editingPlusOne, setEditingPlusOne] = useState(null);
  const [groupSize, setGroupSize] = useState(0);
  const [existingGroupIds, setExistingGroupIds] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [newGuest, setNewGuest] = useState({
    first_name: '',
    last_name: '',
    group_id: '',
    food_choice: '',
    food_restrictions: '',
    attending: null,
    has_plus_one: false,
    rehearsal: false,
    rehearsal_res: null,
    rehearsal_dinner_res: null
  });

  // --- Sanitization helpers ---
  const sanitizeGuest = (g) => {
    if (!g) return null;
    return {
      id: g.id ?? '',
      first_name: g.first_name ?? '',
      last_name: g.last_name ?? '',
      group_id: g.group_id ?? '',
      plus_one_id: g.plus_one_id ?? null,
      attending: g.attending ?? null,
      food_choice: g.food_choice ?? '',
      food_restrictions: g.food_restrictions ?? '',
      rehearsal: g.rehearsal ?? false,
      rehearsal_res: g.rehearsal_res ?? null,
      rehearsal_dinner_res: g.rehearsal_dinner_res ?? null,
      attendance_status: g.attendance_status ?? 2
    };
  };

  const sanitizePlusOne = (p) => {
    if (!p) return null;
    return {
      id: p.id ?? '',
      first_name: p.first_name ?? '',
      last_name: p.last_name ?? '',
      plus_one_id: p.plus_one_id ?? '',
      attending: p.attending ?? null,
      food_choice: p.food_choice ?? '',
      food_restrictions: p.food_restrictions ?? '',
      rehearsal: p.rehearsal ?? false,
      rehearsal_res: p.rehearsal_res ?? null,
      rehearsal_dinner_res: p.rehearsal_dinner_res ?? null,
      attendance_status: p.attendance_status ?? 2
    };
  };

  // --- Combined sanitized list ---
  const allPeople = [...guests, ...plusOnes].filter(Boolean);

  // --- Attendance & food counts ---
  const totalUnresponded = allPeople.filter(p => p?.attendance_status === 2).length;
  const totalYes = allPeople.filter(p => p?.attendance_status !== 2 && p?.attending === true).length;
  const totalNo = allPeople.filter(p => p?.attendance_status !== 2 && p?.attending === false).length;

  const allFoodChoices = [...new Set(allPeople.map(p => p?.food_choice || 'Unspecified'))];
  const foodChoiceCounts = {};
  allFoodChoices.forEach(food => {
    foodChoiceCounts[food] = allPeople.filter(p => (p?.food_choice || 'Unspecified') === food).length;
  });

  const invitedPeople = allPeople.filter(p => p?.rehearsal === true);
  const totalRehearsalYes = allPeople.filter(p => p?.rehearsal_res === true).length;
  const totalRehearsalNo = invitedPeople.filter(p => p?.rehearsal_res === false).length;
  const totalRehearsalNull = invitedPeople.length - totalRehearsalYes - totalRehearsalNo;

  const totalDinnerYes = allPeople.filter(p => p?.rehearsal_dinner_res === true).length;
  const totalDinnerNo = invitedPeople.filter(p => p?.rehearsal_dinner_res === false).length;
  const totalDinnerNull = invitedPeople.length - totalDinnerYes - totalDinnerNo;

  // --- Fetching data ---
  useEffect(() => {
    fetchGuests();
    fetchPlusOnes();

    const interval = setInterval(() => {
    fetchGuests();
    fetchPlusOnes();
  }, 5000);


  }, []);

  const fetchGuests = () => {
    axios.get('https://ww-backend-1-production.up.railway.app/get_all_guests')
      .then(res => setGuests(res.data.guests.map(sanitizeGuest).filter(Boolean)))
      .catch(err => console.error("Failed to fetch guests:", err));
  };

  const fetchPlusOnes = () => {
    axios.get('https://ww-backend-1-production.up.railway.app/get_all_plus_ones')
      .then(res => setPlusOnes(res.data.plus_ones.map(sanitizePlusOne).filter(Boolean)))
      .catch(err => console.error("Failed to fetch plus ones:", err));
  };

  // --- Add guest ---
  const handleAddGuest = async () => {
    try {
      let plus_one_id = null;
      let plusOneObj = null;

      if (newGuest.has_plus_one) {
        const plusOneResponse = await axios.post(
          'https://ww-backend-1-production.up.railway.app/add_plus_one',
          {
            first_name: 'TBD',
            last_name: 'TBD',
            attending: null,
            food_choice: null,
            food_restrictions: null,
            rehearsal: newGuest.rehearsal,
            rehearsal_res: newGuest.attending === false ? false : newGuest.rehearsal_res,
            rehearsal_dinner_res: newGuest.attending === false ? false : newGuest.rehearsal_dinner_res,
          }
        );
        plus_one_id = plusOneResponse.data.plus_one_id;
        plusOneObj = sanitizePlusOne({ ...plusOneResponse.data, plus_one_id });
      }

      const guestResponse = await axios.post(
        'https://ww-backend-1-production.up.railway.app/add_guest',
        {
          first_name: newGuest.first_name,
          last_name: newGuest.last_name,
          group_id: newGuest.group_id || null,
          food_choice: newGuest.food_choice || null,
          food_restrictions: newGuest.food_restrictions || null,
          attending: null,
          plus_one_id,
          rehearsal: newGuest.rehearsal ? true : false,
          rehearsal_res: null,
          rehearsal_dinner_res: null
        }
      );

      setGuests(prev => [...prev, sanitizeGuest(guestResponse.data.guest)]);
      if (plusOneObj) setPlusOnes(prev => [...prev, plusOneObj]);

      setNewGuest({
        first_name: '',
        last_name: '',
        group_id: '',
        food_choice: '',
        food_restrictions: '',
        attending: null,
        has_plus_one: false,
        rehearsal: false,
        rehearsal_res: null,
        rehearsal_dinner_res: null
      });
    } catch (error) {
      console.error('Error adding guest and optional plus one:', error);
    }
  };

  // --- Update/Delete Guest ---
  const handleUpdateGuest = (guest) => {
    setGuests(prev => prev.map(g => g?.id === guest.id ? sanitizeGuest(guest) : g));
    axios.put(`https://ww-backend-1-production.up.railway.app/update_guest/${guest.id}`, guest)
      .catch(err => console.error("Failed to update guest:", err));
    setEditingGuest(null);
  };

  const handleDeleteGuest = (id) => {
    setGuests(prev => prev.filter(g => g?.id !== id));
    axios.delete(`https://ww-backend-1-production.up.railway.app/delete_guest/${id}`)
      .catch(err => console.error("Failed to delete guest:", err));
  };

  // --- Update/Delete Plus One ---
  const handleUpdatePlusOne = (plusOne) => {
    setPlusOnes(prev => prev.map(p => p?.id === plusOne.id ? sanitizePlusOne(plusOne) : p));
    axios.put(`https://ww-backend-1-production.up.railway.app/update_plus_one/${plusOne.id}`, plusOne)
      .catch(err => console.error("Failed to update plus one:", err));
    setEditingPlusOne(null);
  };

  const handleDeletePlusOne = (plusOneId) => {
    if (!plusOneId) return;
    setPlusOnes(prev => prev.filter(p => p?.plus_one_id !== plusOneId));
    axios.delete(`https://ww-backend-1-production.up.railway.app/delete_plus_one/${plusOneId}`)
      .catch(err => console.error("Failed to delete plus one:", err));
  };

  // --- Group management ---
  const handleGroupSizeChange = (e) => {
    const size = parseInt(e.target.value, 10);
    setGroupSize(size);
    setGroupMembers(Array(size).fill().map(() => ({
      first_name: '',
      last_name: '',
      has_plus_one: false,
      rehearsal: false
    })));
  };

  const generateUniqueRandomInt = (min = 1, max = 999999) => {
    let rand;
    do { rand = Math.floor(Math.random() * (max - min + 1)) + min; }
    while (existingGroupIds.includes(rand));
    return rand;
  };

  // --- Group management ---
const handleSubmitGroup = async () => {
  const group_id = generateUniqueRandomInt();

  try {
    // Prepare all members for concurrent submission
    const memberPromises = groupMembers.map(async (member) => {
      let plus_one_id = null;
      let plusOneObj = null;

      if (member.has_plus_one) {
        const res = await axios.post(
          'https://ww-backend-1-production.up.railway.app/add_plus_one',
          {
            first_name: '',
            last_name: '',
            attending: null,
            food_choice: '',
            food_restrictions: '',
            rehearsal: member.rehearsal || false,
            rehearsal_res: null,
            rehearsal_dinner_res: null,
          }
        );
        plus_one_id = res.data.plus_one_id;
        plusOneObj = sanitizePlusOne({ ...res.data, plus_one_id });
        if (plusOneObj) setPlusOnes((prev) => [...prev, plusOneObj]);
      }

      const guestRes = await axios.post(
        'https://ww-backend-1-production.up.railway.app/add_guest',
        {
          ...member,
          group_id,
          plus_one_id,
          food_choice: '',
          food_restrictions: '',
          attending: null,
          rehearsal: member.rehearsal || false,
          rehearsal_res: null,
          rehearsal_dinner_res: null,
        }
      );

      return sanitizeGuest(guestRes.data.guest);
    });

    // Wait for all members to finish at the same time
    const newGuests = await Promise.all(memberPromises);

    // Add all guests to state at once
    setGuests((prev) => [...prev, ...newGuests]);
    setGroupSize(0);
    setGroupMembers([]);
  } catch (error) {
    console.error('Error adding group members:', error);
  }
};


  const updateGroupMember = (index, field, value) => {
    const updated = [...groupMembers];
    updated[index][field] = value;
    setGroupMembers(updated);
  };

  const filteredGuests = guests.filter(g =>
    g?.first_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
    g?.last_name?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  const filteredPlusOnes = plusOnes.filter(p =>
    p?.first_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
    p?.last_name?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  
  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem' }}>Admin Portal</h1>

      <input
        placeholder="Search by name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ padding: '0.5rem', marginBottom: '1rem', width: '100%', maxWidth: '400px' }}
      />

      {/* Add Guest & Group sections (unchanged) */}
      {/* --- Add New Guest --- */}
      <h2 style={{ marginTop: '2rem' }}>Add New Guest</h2>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input placeholder="First Name" value={newGuest.first_name} onChange={e => setNewGuest({ ...newGuest, first_name: e.target.value })} />
        <input placeholder="Last Name" value={newGuest.last_name} onChange={e => setNewGuest({ ...newGuest, last_name: e.target.value })} />
        <label>
          Plus One?
          <input type="checkbox" checked={newGuest.has_plus_one} onChange={e => setNewGuest({ ...newGuest, has_plus_one: e.target.checked })} />
        </label>
        <label>
          Invited to Rehearsal?
          <input type="checkbox" checked={newGuest.rehearsal} onChange={(e) => setNewGuest({ ...newGuest, rehearsal: e.target.checked })} />
        </label>
      </div>
      <button onClick={handleAddGuest}>Add Guest</button>

      {/* --- Add Group --- */}
      <h2 style={{ marginTop: '2rem' }}>Add Group</h2>
      <input type="number" min="1" placeholder="Number in group" value={groupSize} onChange={handleGroupSizeChange} />
      {groupMembers.map((member, index) => (
        <div key={index} style={{ marginBottom: '1rem', marginTop: '1rem', padding: '1rem', border: '1px solid #ccc' }}>
          <h4>Member {index + 1}</h4>
          <input placeholder="First Name" value={member.first_name} onChange={(e) => updateGroupMember(index, 'first_name', e.target.value)} />
          <input placeholder="Last Name" value={member.last_name} onChange={(e) => updateGroupMember(index, 'last_name', e.target.value)} />
          <label>
            Plus One?
            <input type="checkbox" checked={member.has_plus_one} onChange={(e) => updateGroupMember(index, 'has_plus_one', e.target.checked)} />
            <label style={{ marginLeft: '1rem' }}>
              Invited to Rehearsal?
              <input type="checkbox" checked={member.rehearsal || false} onChange={(e) => updateGroupMember(index, 'rehearsal', e.target.checked)} />
            </label>
          </label>
        </div>
      ))}
      {groupSize > 0 && <button onClick={handleSubmitGroup}>Submit Group</button>}

      {/* Guests Table */}
      <h2 style={{ marginTop: '2rem' }}>Guests</h2>
      <table className="table">
        <thead>
          <tr>
            <th>First Name</th><th>Last Name</th><th>Group ID</th><th>Plus One ID</th><th>Attending</th>
            <th>Food Choice</th><th>Food Restrictions</th><th>Rehearsal?</th><th>Rehearsal Response</th>
            <th>Rehearsal Dinner Response</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredGuests.map(g => (
            <tr key={g.id}>
              {editingGuest?.id === g.id ? (
                <>
                  <td><input value={editingGuest.first_name} onChange={e => setEditingGuest({ ...editingGuest, first_name: e.target.value })} /></td>
                  <td><input value={editingGuest.last_name} onChange={e => setEditingGuest({ ...editingGuest, last_name: e.target.value })} /></td>
                  <td><input value={editingGuest.group_id} onChange={e => setEditingGuest({ ...editingGuest, group_id: e.target.value })} /></td>
                  <td><input value={editingGuest.plus_one_id} onChange={e => setEditingGuest({ ...editingGuest, plus_one_id: e.target.value })} /></td>
                  <td><input value={editingGuest.attending} onChange={e => setEditingGuest({ ...editingGuest, attending: e.target.value })} /></td>
                  <td><input value={editingGuest.food_choice} onChange={e => setEditingGuest({ ...editingGuest, food_choice: e.target.value })} /></td>
                  <td><input value={editingGuest.food_restrictions} onChange={e => setEditingGuest({ ...editingGuest, food_restrictions: e.target.value })} /></td>
                  <td><input value={editingGuest.rehearsal} onChange={e => setEditingGuest({ ...editingGuest, rehearsal: e.target.value })} /></td>
                  <td><input value={editingGuest.rehearsal_res} onChange={e => setEditingGuest({ ...editingGuest, rehearsal_res: e.target.value })} /></td>
                  <td><input value={editingGuest.rehearsal_dinner_res} onChange={e => setEditingGuest({ ...editingGuest, rehearsal_dinner_res: e.target.value })} /></td>
                  <td><button onClick={() => handleUpdateGuest(editingGuest)} title="Save"><FaSave /></button></td>
                </>
              ) : (
                <>
                  <td>{g.first_name}</td>
                  <td>{g.last_name}</td>
                  <td>{g.group_id}</td>
                  <td>{g.plus_one_id}</td>
                  <td>{g.attendance_status === 2 ? 'No Response' : g.attending === true ? 'Yes' : g.attending === false ? 'No' : ''}</td>
                  <td>{g.food_choice}</td>
                  <td>{g.food_restrictions}</td>
                  <td>{g.rehearsal ? 'Yes' : 'No'}</td>
                  <td>{g.rehearsal_res === true ? 'Accept' : g.rehearsal_res === false ? 'Decline' : ''}</td>
                  <td>{g.rehearsal_dinner_res === true ? 'Accept' : g.rehearsal_dinner_res === false ? 'Decline' : ''}</td>
                  <td>
                    <button onClick={() => setEditingGuest(g)} title="Edit"><FaEdit /></button>
                    <button onClick={() => handleDeleteGuest(g.id)} title="Delete"><FaTrash /></button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Plus Ones Table */}
      <h2>Plus Ones</h2>
      <table className="table">
        <thead>
          <tr>
            <th>First Name</th><th>Last Name</th><th>Plus One ID</th><th>Attending</th>
            <th>Food Choice</th><th>Food Restrictions</th><th>Rehearsal?</th><th>Rehearsal Response</th>
            <th>Rehearsal Dinner Response</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredPlusOnes.map(p => (
            <tr key={p.id}>
              {editingPlusOne && editingPlusOne.id === p.id ? (
                <>
                  <td><input value={editingPlusOne.first_name || ''} onChange={e => setEditingPlusOne({ ...editingPlusOne, first_name: e.target.value })} /></td>
                  <td><input value={editingPlusOne.last_name || ''} onChange={e => setEditingPlusOne({ ...editingPlusOne, last_name: e.target.value })} /></td>
                  <td>{p.plus_one_id}</td>
                  <td><input value={editingPlusOne.attending || ''} onChange={e => setEditingPlusOne({ ...editingPlusOne, attending: e.target.value })} /></td>
                  <td><input value={editingPlusOne.food_choice || ''} onChange={e => setEditingPlusOne({ ...editingPlusOne, food_choice: e.target.value })} /></td>
                  <td><input value={editingPlusOne.food_restrictions || ''} onChange={e => setEditingPlusOne({ ...editingPlusOne, food_restrictions: e.target.value })} /></td>
                  <td><input value={editingPlusOne.rehearsal} onChange={e => setEditingPlusOne({ ...editingPlusOne, rehearsal: e.target.value })} /></td>
                  <td><input value={editingPlusOne.rehearsal_res} onChange={e => setEditingPlusOne({ ...editingPlusOne, rehearsal_res: e.target.value })} /></td>
                  <td><input value={editingPlusOne.rehearsal_dinner_res} onChange={e => setEditingPlusOne({ ...editingPlusOne, rehearsal_dinner_res: e.target.value })} /></td>
                  <td><button onClick={() => handleUpdatePlusOne(editingPlusOne)} title="Save"><FaSave /></button></td>
                </>
              ) : (
                <>
                  <td>{p.first_name || ''}</td>
                  <td>{p.last_name || ''}</td>
                  <td>{p.plus_one_id}</td>
                  <td>{p.attendance_status === 2 ? 'No Response' : p.attending === true ? 'Yes' : p.attending === false ? 'No' : '' }</td>
                  <td>{p.food_choice || ''}</td>
                  <td>{p.food_restrictions || ''}</td>
                  <td>{p.rehearsal ? 'Yes' : 'No'}</td>
                  <td>{p.rehearsal_res === true ? 'Accept' : p.rehearsal_res === false ? 'Decline' : ''}</td>
                  <td>{p.rehearsal_dinner_res === true ? 'Accept' : p.rehearsal_dinner_res === false ? 'Decline' : ''}</td>
                  <td>
                    <button onClick={() => setEditingPlusOne(p)} title="Edit"><FaEdit /></button>
                    <button onClick={() => handleDeletePlusOne(p.plus_one_id)} title="Delete"><FaTrash /></button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

            {/* Attendance & Food Summary */}
            <h3 style={{ marginTop: '2rem' }}>Attendance & Food Summary</h3>
      <table className="table">
        <thead>
          <tr>
            <th>Yes</th>
            <th>No</th>
            <th>No Response</th>
            {allFoodChoices.map(food => (<th key={food}>{food}</th>))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ backgroundColor: '#d4edda' }}>{totalYes}</td>
            <td style={{ backgroundColor: '#f8d7da' }}>{totalNo}</td>
            <td style={{ backgroundColor: '#fff3cd' }}>{totalUnresponded}</td>
            {allFoodChoices.map(food => (<td key={food}>{foodChoiceCounts[food]}</td>))}
          </tr>
        </tbody>
      </table>

      {/* Rehearsal Summary */}
      <h3 style={{ marginTop: '2rem' }}>Rehearsal Summary</h3>
      <table className="table">
        <thead>
          <tr>
            <th>Yes</th>
            <th>No</th>
            <th>No Response</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{totalRehearsalYes}</td>
            <td>{totalRehearsalNo}</td>
            <td>{totalRehearsalNull}</td>
          </tr>
        </tbody>
      </table>

      {/* Rehearsal Dinner Summary */}
      <h3 style={{ marginTop: '2rem' }}>Rehearsal Dinner Summary</h3>
      <table className="table">
        <thead>
          <tr>
            <th>Yes</th>
            <th>No</th>
            <th>No Response</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{totalDinnerYes}</td>
            <td>{totalDinnerNo}</td>
            <td>{totalDinnerNull}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

